import { Plus, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import $api from "@/zapi-frontend/shared/api/axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { showAppToast } from "@/zapi-frontend/shared/ui/AppToast";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { getRouteNewVersion, getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { useProjectVersionsStore } from "../../model/projectVersionsStore";
import { VersionRowV2 } from "./_VersionRowV2";
import { NewVersionModalV2 } from "./_NewVersionModalV2";
import { RenameVersionModal } from "./_RenameVersionModal";
import { DeleteVersionModal } from "./_DeleteVersionModal";
import { useTranslation } from "react-i18next";

export type TVersionHistoryItem = {
  id: string;
  name: string;
  date: string;
  lastModified?: string;
  modifiedBy?: string;
  chaptersGenerated?: number;
  chaptersTotal?: number;
  isGenerating?: boolean;
  framingPercent?: number;
  isDraft?: boolean;
};

type TProps = {
  rowProjectId: string;
  projectName?: string;
  projectLogoUrl?: string | null;
  versions: TVersionHistoryItem[];
  hasEdit: boolean;
  organizationId: number;
  openInNewTab?: boolean;
  selectedVersionId?: string;
  onSelectVersion?: (versionId: string) => void;
  onCurrentVersionRemoved?: (nextVersionId: string | null) => void;
  onInvalidated: () => void;
  onOptimisticRename?: (versionId: string, newName: string) => void;
};

export function VersionHistoryPanel(props: TProps) {
  const { t } = useTranslation("common");
  const {
    rowProjectId,
    projectName,
    projectLogoUrl,
    versions,
    hasEdit,
    organizationId,
    openInNewTab = true,
    selectedVersionId,
    onSelectVersion,
    onCurrentVersionRemoved,
    onInvalidated,
    onOptimisticRename
  } = props;

  const navigate = useNavigate();

  const optimisticRename = useProjectVersionsStore((s) => s.optimisticRename);
  const optimisticAdd = useProjectVersionsStore((s) => s.optimisticAdd);
  const optimisticRemove = useProjectVersionsStore((s) => s.optimisticRemove);
  const inline = useProjectVersionsStore((s) => s.inline);
  const setInline = useProjectVersionsStore((s) => s.setInline);
  const patchInline = useProjectVersionsStore((s) => s.patchInline);

  const ourInline = inline?.rowId === rowProjectId ? inline : null;
  const renameId = ourInline?.mode === "rename" ? ourInline.targetId : null;
  const renameValue = ourInline?.mode === "rename" ? ourInline.value : "";
  const deleteConfirmId = ourInline?.mode === "delete" ? ourInline.targetId : null;
  const newVersionOpen = ourInline?.mode === "new";
  const duplicateSourceId = newVersionOpen ? ourInline.sourceId : null;
  const duplicatePending = newVersionOpen && ourInline.pending;
  const newVersionButtonPending = duplicatePending && duplicateSourceId === null;
  const renamePending = ourInline?.mode === "rename" && ourInline.pending;
  const deletePending = ourInline?.mode === "delete" && ourInline.pending;

  const newestId = versions[0]?.id;
  // Deleting the last version would take the whole project with it (INV-2342).
  const canDeleteVersion = versions.length > 1;
  const isPending = duplicatePending || renamePending || deletePending;
  const hasInlineEdit = ourInline !== null;
  const isRowActionsDisabled = hasInlineEdit || isPending;

  const setRenameValue = (v: string) => patchInline({ value: v });

  const openNewVersion = () => {
    if (hasInlineEdit) return;
    if (!newestId) {
      toast.error(t("versions.errors.noProject"));
      return;
    }
    setInline({ rowId: rowProjectId, mode: "new", value: "", sourceId: null, targetId: null, pending: false });
  };

  const closeNewVersion = () => {
    if (ourInline?.mode === "new") setInline(null);
  };

  const openRename = (id: string, currentName: string) => {
    if (hasInlineEdit) return;
    setInline({ rowId: rowProjectId, mode: "rename", value: currentName, sourceId: null, targetId: id, pending: false });
  };

  const closeRename = () => {
    if (ourInline?.mode === "rename") setInline(null);
  };

  const openDeleteConfirm = (id: string) => {
    if (hasInlineEdit || !canDeleteVersion) return;
    setInline({ rowId: rowProjectId, mode: "delete", value: "", sourceId: null, targetId: id, pending: false });
  };

  const closeDeleteConfirm = () => {
    if (ourInline?.mode === "delete") setInline(null);
  };

  const createVersion = (sourceId: string, name: string, isDraft: boolean) => {
    if (!sourceId) {
      toast.error(t("versions.errors.noProject"));
      return;
    }
    void duplicateFrom(sourceId, name, isDraft);
  };

  const saveRename = async (id: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error(t("versions.errors.nameRequired"));
      return;
    }
    patchInline({ pending: true });
    const [, err] = await tryCatch<void, AxiosError<{ message: string }>>(
      $api.patch(`/projects/${id}`, { versionDisplayName: trimmed })
    );
    if (err) {
      toast.error(err.response?.data?.message || t("versions.errors.renameFailed"));
      patchInline({ pending: false });
      return;
    }
    showAppToast({ message: t("versions.renamedToast", { name: trimmed }) });
    optimisticRename(rowProjectId, id, trimmed);
    onOptimisticRename?.(id, trimmed);
    setInline(null);
    onInvalidated();
  };

  const restoreVersion = async (targetId: string, wasCurrent: boolean) => {
    const [, err] = await tryCatch<void, AxiosError<{ message: string }>>(
      $api.post(`/projects/${rowProjectId}/versions/restore`, { targetProjectId: Number(targetId) })
    );
    if (err) {
      toast.error(err.response?.data?.message || t("versions.errors.restoreFailed"));
      return;
    }
    if (wasCurrent) {
      navigate(getRouteProjectDetails(targetId));
      return;
    }
    onInvalidated();
  };

  const confirmDelete = async (targetId: string) => {
    patchInline({ pending: true });
    const [, err] = await tryCatch<void, AxiosError<{ message: string }>>(
      $api.post(`/projects/${rowProjectId}/versions/remove`, { targetProjectId: Number(targetId) })
    );
    if (err) {
      toast.error(err.response?.data?.message || t("versions.errors.deleteFailed"));
      patchInline({ pending: false });
      return;
    }

    const wasCurrent = !!onCurrentVersionRemoved && targetId === (selectedVersionId ?? rowProjectId);
    showAppToast({
      tone: "danger",
      message: t("versions.deletedToast", {
        name: versions.find((v) => v.id === targetId)?.name ?? targetId
      }),
      action: { label: t("versions.undo"), onClick: () => void restoreVersion(targetId, wasCurrent) }
    });
    optimisticRemove(rowProjectId, targetId);
    setInline(null);

    if (wasCurrent) {
      onCurrentVersionRemoved?.(versions.find((v) => v.id !== targetId)?.id ?? null);
      return;
    }

    onInvalidated();
  };

  const duplicateFrom = async (sourceId: string, initialName: string, inputsOnly = false) => {
    const trimmed = initialName.trim();
    patchInline({ pending: true });
    const endpoint = inputsOnly ? `/projects/${sourceId}/duplicate-inputs` : `/projects/${sourceId}/duplicate`;
    const body = inputsOnly
      ? trimmed
        ? { initialName: trimmed }
        : {}
      : { organizationId, ...(trimmed ? { initialName: trimmed } : {}) };
    const [result, err] = await tryCatch<{ data: { data: { id: number } } }, AxiosError<{ message: string }>>(
      $api.post(endpoint, body)
    );
    if (err) {
      toast.error(err.response?.data?.message || t("versions.errors.duplicateFailed"));
      if (inputsOnly) {
        setInline(null);
      } else {
        patchInline({ pending: false });
      }
      return;
    }
    const createdName = trimmed || initialName || "Initial";
    const createdId = result?.data?.data?.id;
    const showCreatedToast = (target: string) =>
      showAppToast({
        message: t("versions.createdToast", { name: createdName }),
        action: { label: t("versions.view"), onClick: () => navigate(target) }
      });

    if (inputsOnly && createdId) {
      setInline(null);
      showCreatedToast(getRouteNewVersion(createdId));
      navigate(getRouteNewVersion(createdId));
      return;
    }
    optimisticAdd(rowProjectId, createdName);
    setInline(null);
    onInvalidated();
    if (createdId) {
      const target = `${getRouteProjectDetails(createdId)}?view=projectConfig`;
      showCreatedToast(target);
      navigate(target);
    }
  };

  return (
    <div
      // inset ring, not a border: Figma's 286px frame draws its stroke inside
      className="flex flex-col overflow-hidden rounded-lg bg-white w-[286px] p-1 shadow-300 ring-1 ring-inset ring-innoveraZinc-150"
      style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      data-prevent-row-nav
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {newVersionOpen && (
        <NewVersionModalV2
          open
          projectName={projectName}
          projectLogoUrl={projectLogoUrl}
          versions={versions}
          defaultSourceId={duplicateSourceId ?? newestId ?? versions[0]?.id ?? ""}
          isLoading={duplicatePending}
          onCancel={closeNewVersion}
          onCreate={createVersion}
        />
      )}

      <div className="flex-1 space-y-1 overflow-y-auto p-0">
        {versions.map((v) => (
          <VersionRowV2
            key={v.id}
            version={v}
            isCurrent={v.id === (selectedVersionId ?? rowProjectId)}
            hasEdit={hasEdit}
            isActionsDisabled={isRowActionsDisabled}
            canDelete={canDeleteVersion}
            openInNewTab={openInNewTab}
            onSelect={onSelectVersion}
            onRename={() => openRename(v.id, v.name)}
            onDelete={() => openDeleteConfirm(v.id)}
          />
        ))}
      </div>
      </div>

      {hasEdit && newestId && !newVersionOpen && (
        <div className="shrink-0 p-1">
          <button
            type="button"
            disabled={isRowActionsDisabled}
            onClick={openNewVersion}
            className={cn(
              "flex h-8 w-full items-center justify-center gap-1 rounded-lg bg-innoveraZinc-100 px-3",
              isRowActionsDisabled ? "cursor-not-allowed opacity-60" : "hover:bg-innoveraZinc-150"
            )}
          >
            {newVersionButtonPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <FigmaTypography component="span" variant="label-2" color="zinc900">
              {t("versions.newTitle")}
            </FigmaTypography>
          </button>
        </div>
      )}

      <RenameVersionModal
        isOpen={renameId !== null}
        projectName={projectName}
        projectLogoUrl={projectLogoUrl}
        value={renameValue}
        isLoading={renamePending}
        onChange={setRenameValue}
        onCancel={closeRename}
        onSave={() => renameId && void saveRename(renameId)}
      />
      <DeleteVersionModal
        isOpen={deleteConfirmId !== null}
        versionName={versions.find((v) => v.id === deleteConfirmId)?.name ?? "this version"}
        projectName={projectName}
        isLoading={deletePending}
        onCancel={closeDeleteConfirm}
        onConfirm={() => deleteConfirmId && void confirmDelete(deleteConfirmId)}
      />
    </div>
  );
}
